import type { ID } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { SocketActions } from '@store/socketSaga'
import {
  clearSelectedThread,
  selectSelectedThread,
} from '@storeOld/reducers/canvas/canvas'
import { closeModal } from '@storeOld/reducers/ui'
import { call, put, select } from 'redux-saga/effects'

import { NestClient } from '@/nest'
import {
  type CommentReadsActions,
  InternalCommentReadsActions,
} from '../actions'
import { BASELINE_TARGET, UNREAD } from '../constants'
import { selectCommentReads, selectOpenTargets } from '../selectors'
import type { CommentReadsState } from '../types'

// Mark as unread: the mark of the place goes back to zero, so everything
// there is unread again until the place is opened next time. An open place
// is closed first — whatever stays open is re-marked read on the next
// comment that arrives anywhere
export function* markUnread({
  payload: { target },
}: ReturnType<typeof CommentReadsActions.markUnread>) {
  const reads: CommentReadsState = yield select(selectCommentReads)

  // no baseline — a guest, or the list has not arrived yet
  if (reads[BASELINE_TARGET] === undefined) {
    return
  }

  const open: ID[] = yield select(selectOpenTargets)

  if (open.includes(target)) {
    const selectedThread: ID | null = yield select(selectSelectedThread)

    // a target is either a pin or the block or page behind the modal
    yield put(selectedThread === target ? clearSelectedThread() : closeModal())
  }

  const projectId: ID = yield select(migrationProjectSelector.getProjectId)

  const mark = InternalCommentReadsActions.update({ target, readAt: UNREAD })

  yield put(mark)

  // the same reset to this user's other tabs and devices, through their room
  yield put(SocketActions.emit({ eventName: 'comment-read', payload: mark }))

  try {
    yield call(NestClient.projects.commentReads.mark, projectId, target, {
      readAt: UNREAD,
    })
  } catch (error) {
    console.error('Error marking comments unread:', error)
  }
}
